import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { RotateCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

/**
 * Whether any query under `queryKeys` is in flight — the match the button spins
 * on, exported so a caller can say so somewhere less peripheral than a 14px
 * icon. A key matches by prefix, so `["/clubs"]` covers `["/clubs", "infinite"]`.
 */
export function useFetchingKeys(queryKeys: readonly (readonly unknown[])[]): boolean {
  return (
    useIsFetching({
      predicate: (query) =>
        queryKeys.some((key) =>
          key.every((part, index) => Object.is(query.queryKey[index], part)),
        ),
    }) > 0
  );
}

/**
 * Refetches every query under any of `queryKeys`; spins while one is in flight.
 *
 * Labelled rather than icon-only: it sits beside a worded "Add …" action, and a
 * lone glyph next to a written one reads as decoration. Ghost, because it is
 * housekeeping — the action beside it is the one the screen is for.
 *
 * The label stays put while fetching and the icon turns instead: swapping the
 * word for a spinner would make the button change width mid-press.
 */
export function RefreshButton({
  queryKeys,
  className,
}: {
  /** Readonly, so a key written `as const` is a key this accepts. */
  queryKeys: readonly (readonly unknown[])[];
  className?: string;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const fetching = useFetchingKeys(queryKeys);
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-busy={fetching || undefined}
      onClick={() => {
        for (const queryKey of queryKeys) void queryClient.invalidateQueries({ queryKey });
      }}
      className={className}
    >
      <RotateCw className={cn(fetching && "animate-spin")} aria-hidden />
      {t("rp.refresh")}
    </Button>
  );
}
